import React, { useState, useEffect } from "react";
import api from "@/api";
import CSVImportModal from "./modals/CSVImportModal";
import PositionsImportModal from "./modals/PositionsImportModal";
import UploadProgressModal from "./modals/UploadProgressModal";

/**
 * Peek at the first line of a CSV file and classify it.
 * Returns a Promise<"positions" | "transactions" | "unknown">.
 */
function detectCsvType(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const firstLine = (e.target.result || "").split("\n")[0];
      if (
        firstLine.includes("Account Number") &&
        firstLine.includes("Investment Type")
      ) {
        resolve("positions");
      } else if (
        firstLine.includes("Run Date") &&
        firstLine.includes("Action")
      ) {
        resolve("transactions");
      } else {
        resolve("unknown");
      }
    };
    reader.onerror = () => resolve("unknown");
    // Only need the first ~500 bytes to read the header line
    reader.readAsText(file.slice(0, 500));
  });
}

/**
 * GlobalDropZone - Allows dropping screenshots/CSV anywhere on the page
 *
 * Features:
 * - Detects file type (image vs CSV)
 * - CSV: Shows confirmation modal with account selection
 * - Images: Auto-uploads to user-level endpoint
 * - Shows visual feedback during drag
 * - Displays processing status modal
 */
const GlobalDropZone = ({ accounts, currentAccountId, onUploadComplete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [showPositionsModal, setShowPositionsModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);

  // Handle drag events on entire page
  useEffect(() => {
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Only hide if leaving the window (not just a child element)
      if (e.clientX === 0 && e.clientY === 0) {
        setIsDragging(false);
      }
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const fileArray = Array.from(files);
        fileArray.forEach((file) => handleFileDetection(file));
      }
    };

    // Attach listeners to document
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("dragleave", handleDragLeave);
    document.addEventListener("drop", handleDrop);

    return () => {
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("dragleave", handleDragLeave);
      document.removeEventListener("drop", handleDrop);
    };
  }, []);

  // Detect file type and route appropriately
  const handleFileDetection = async (file) => {
    if (!file) return;

    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    // Check if CSV
    if (
      fileType === "text/csv" ||
      fileType === "application/vnd.ms-excel" ||
      fileName.endsWith(".csv")
    ) {
      const csvType = await detectCsvType(file);
      setCsvFile(file);
      if (csvType === "positions") {
        setShowPositionsModal(true);
      } else {
        // transactions or unknown — use existing flow
        setShowCSVModal(true);
      }
    } else if (fileType.startsWith("image/")) {
      // Auto-upload image immediately
      handleImageUpload(file);
    } else {
      alert(`Unsupported file type: ${fileType || "unknown"}`);
    }
  };

  // Handle image upload to user-level endpoint
  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Add to uploads list for status tracking
      const newUpload = {
        id: response.data.upload_id,
        filename: file.name,
        status: "processing",
      };
      setUploads((prev) => [newUpload, ...prev]);

      // Poll user-level endpoint for status
      pollUploadStatus(response.data.upload_id);
    } catch (error) {
      console.error("Upload failed:", error);
      alert(`Failed to upload: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Poll upload status for user-level uploads
  const pollUploadStatus = async (uploadId) => {
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      try {
        // Poll user-level endpoint (no account_id needed)
        const response = await api.get("/uploads");
        const upload = response.data.find((u) => u.id === uploadId);

        if (upload) {
          setUploads((prev) =>
            prev.map((u) =>
              u.id === uploadId ? { ...u, status: upload.processing_status } : u
            )
          );

          if (upload.processing_status === "completed") {
            if (onUploadComplete) onUploadComplete(upload);

            // Remove from list after 3 seconds
            setTimeout(() => {
              setUploads((prev) => prev.filter((u) => u.id !== uploadId));
            }, 3000);
            return;
          } else if (upload.processing_status === "failed") {
            alert(`Processing failed: ${upload.processing_error}`);
            // Remove from list after 5 seconds
            setTimeout(() => {
              setUploads((prev) => prev.filter((u) => u.id !== uploadId));
            }, 5000);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        }
      } catch (error) {
        console.error("Error polling upload status:", error);
      }
    };

    poll();
  };

  return (
    <>
      {/* Drag overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-blue-500 bg-opacity-10 border-4 border-dashed border-blue-500 z-[9999] flex items-center justify-center pointer-events-none">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl text-center">
            <div className="text-5xl mb-4">📸</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Drop to Upload
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Screenshots will be analyzed by AI
              <br />
              CSV files will prompt for account selection
            </p>
          </div>
        </div>
      )}

      {/* CSV Import Modal (transaction history) */}
      {showCSVModal && csvFile && (
        <CSVImportModal
          file={csvFile}
          accounts={accounts || []}
          defaultAccountId={currentAccountId}
          onClose={() => {
            setShowCSVModal(false);
            setCsvFile(null);
          }}
          onSuccess={() => {
            if (onUploadComplete) onUploadComplete();
          }}
        />
      )}

      {/* Positions Import Modal (Portfolio Positions export) */}
      {showPositionsModal && csvFile && (
        <PositionsImportModal
          file={csvFile}
          accounts={accounts || []}
          onClose={() => {
            setShowPositionsModal(false);
            setCsvFile(null);
          }}
          onSuccess={() => {
            if (onUploadComplete) onUploadComplete();
          }}
        />
      )}

      {/* Upload Progress Modal */}
      <UploadProgressModal uploads={uploads} />
    </>
  );
};

export default GlobalDropZone;
